import { Button, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import DeleteDialogIcon from "@entrilia/shared/icons/DeleteDialogIcon";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../api/adminApi";

interface Props {
  consentName: string;
  open: boolean;
  onClose: () => void;
  onDeleted: (consentName: string) => void;
}

const deleteUserConsent = withErrorHandling(adminApi.deleteUserConsent);

const DeleteUserConsentDialog = ({ consentName, open, onClose, onDeleted }: Props) => {
  const { sendNotification, sendNotificationError } = useNotificationContext();

  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    setSaving(true);
    const [, error] = await deleteUserConsent(consentName);
    setSaving(false);

    if (error) {
      logError(error, "[DeleteUserConsentDialog] deleteUserConsent");
      sendNotificationError(error.message);
      return;
    }

    sendNotification("User consent deleted successfully");
    onDeleted(consentName);
  };

  return (
    <SmallDialog open={open} onClose={onClose}>
      <DialogTitle>Delete User Consent</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <DialogContent>
        <Stack spacing={2} sx={{ alignItems: "center" }}>
          <DeleteDialogIcon sx={{ width: 80, height: 80 }} />
          <Stack spacing={1} sx={{ alignItems: "center" }}>
            <Typography noWrap variant="h6">
              Delete user consent?
            </Typography>
            <Typography color="textSecondary" sx={{ textAlign: "center" }}>
              You&apos;re about to delete user consent <strong>{consentName}</strong>.
            </Typography>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogButtons onCancel={onClose}>
        <Button variant="outlined" color="error" loading={saving} onClick={handleSubmit}>
          Delete
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default DeleteUserConsentDialog;
